/**
 * Pure helpers for the POC Claims tab (renderPocClaims in FundingRequestDetail).
 *
 * Extracted so the disambiguation logic is unit-testable without importing the
 * cloudscape-heavy FundingRequestDetail component (which can't be imported
 * under CRA 5's default jest transform — cloudscape ships ESM).
 */

/**
 * Parse the POC agent's persisted `validation_results_json` (a JSON *string*
 * as make_decision.py writes it, but tolerate an already-parsed object or a
 * missing/blank value). Never throws.
 *
 * @param {string|object|null|undefined} raw
 * @returns {{decision?: string, reasons: Array, attached_files: object, checks: object}}
 */
export function parsePocResults(raw) {
  let results = {};
  try {
    results = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
  } catch (e) {
    results = {};
  }
  if (!results || typeof results !== 'object') results = {};
  return {
    decision: results.decision,
    // The poc_sop version this row was validated against (make_decision.py
    // persists it). Used to gate a green "Passed": a per-field check is only
    // trusted when the SOP version that introduced it was in force when the
    // row was produced. Older/unversioned rows -> undefined -> never green.
    sop_version: typeof results.sop_version === 'string' ? results.sop_version : undefined,
    reasons: Array.isArray(results.reasons) ? results.reasons : [],
    attached_files: results.attached_files || {},
    checks: results.checks || {},
  };
}

/**
 * The value the POC tab icon (getTabIcon) must be driven from.
 *
 * make_decision.py persists TWO outcome fields on approval_summary:
 *   - decision:       approve | reject | flag_for_manual_review   (internal code)
 *   - recommendation: recommend_approval | recommend_rejection | needs_review
 * getTabIcon only recognizes the recommend_* vocabulary, so both the Approval
 * Summary and Claims tab icons must read `recommendation` — reading `decision`
 * falls through to the ⚠️ default for every outcome (batpur@ CR-295319668).
 *
 * @param {object|null|undefined} approvalSummary  request.approval_summary
 * @returns {string|undefined} the recommend_* status, or undefined if absent
 */
export function pocTabIconStatus(approvalSummary) {
  return (approvalSummary || {}).recommendation;
}

/**
 * Resolve the POC CST card's VALIDATION RESULT (policy outcome) — distinct from
 * its processing status (batpur@ CR-295319668 rev 6).
 *
 * _process_classified() writes status:"validated" whenever extraction + the
 * python_validator merely COMPLETE — including when the validator returned
 * decision:"reject" with error findings. So `status` is a processing state, NOT
 * a pass/fail; rendering the indicator off `status` shows a failed-and-rejected
 * CST as green. Derive the policy result from decision + findings instead.
 *
 * Precedence (first match wins):
 *   1. any error-severity finding        -> error   / "Failed"
 *   2. decision reject                    -> error   / "Failed"
 *   3. decision attention|flag_for_manual_review -> warning / "Manual review"
 *   4. decision ok|pass, no error finding -> success / "Passed"
 *   5. status parse_error|vision_unavailable -> warning / "Could not validate"
 *   6. anything else (incl. bare status:"validated" w/ no decision) -> warning
 *      / "Unknown" — NEVER green by default.
 *
 * Invariant: green ("success"/"Passed") ONLY when the persisted policy outcome
 * explicitly passed AND no error finding exists.
 *
 * @param {object|null|undefined} cst  the customer_sign_off validations entry
 * @returns {{ type: 'success'|'error'|'warning', label: string }}
 */
export function resolvePocCstResult(cst) {
  const entry = cst || {};
  const findings = Array.isArray(entry.findings) ? entry.findings : [];
  const decision = (entry.decision || '').toString().toLowerCase();
  const status = (entry.status || '').toString().toLowerCase();

  if (findings.some((f) => f && f.severity === 'error')) {
    return { type: 'error', label: 'Failed' };
  }
  if (decision === 'reject') {
    return { type: 'error', label: 'Failed' };
  }
  if (decision === 'attention' || decision === 'flag_for_manual_review') {
    return { type: 'warning', label: 'Manual review' };
  }
  if ((decision === 'ok' || decision === 'pass')) {
    return { type: 'success', label: 'Passed' };
  }
  if (status === 'parse_error' || status === 'vision_unavailable') {
    return { type: 'warning', label: 'Could not validate' };
  }
  return { type: 'warning', label: 'Unknown' };
}

/**
 * The POC CST field -> SOP-check map (source of truth: poc_validation_agent/
 * sop/poc_sop.yaml `validation_taxonomy` + the cst_validators finding codes).
 *
 * Each entry describes ONE validated CST field: the label, its SOP section, the
 * finding code(s) the agent emits against it, and how to read its display value
 * from the extracted CST data. This is what lets the Claims tab separate "fields
 * the SOP validates" from "fields shown for context only" (see
 * POC_CST_INFO_FIELDS) and flag anything extracted that neither list covers
 * (collectOtherCstFields).
 *
 * Hard-coded here rather than derived at runtime because the SOP defines the
 * requirement, not the data. Keep in sync with poc_sop.yaml if the SOP changes.
 *
 * `conditional: true` marks a check that only applies in some cases (funding
 * amount is Public-Sector-only) — resolveCstFieldChecks renders it as N/A rather
 * than pass/fail when it doesn't apply.
 *
 * `introduced` is the poc_sop.yaml version in which the check first existed. A
 * green "Passed" (or "N/A") is only shown when the row's persisted sop_version
 * is >= this — presence of a value is NOT proof the check ran, since an older
 * row was validated before the check existed (batpur@ CR-298110147). Signature-
 * presence and WWPS predate the versioned checks (in force at 0.1.0).
 */
export const POC_CST_CHECK_FIELDS = [
  { key: 'project_name', label: 'Project Name', sop: '§3 CST-1', introduced: '0.2.0',
    codes: ['CSTNoProjectName'],
    value: (d) => d.project_name },
  { key: 'claim_fund_request_id', label: 'Fund Request ID', sop: '§3 CST-1', introduced: '0.2.0',
    codes: ['CSTNoFRID', 'CSTInvalidFRID'],
    value: (d) => d.claim_fund_request_id },
  { key: 'dates', label: 'Actual Start / End Date', sop: '§3 CST-1', introduced: '0.2.0',
    codes: ['CSTNoDate'],
    // Both dates must be present; show them together.
    value: (d) => (d.start_date || d.end_date)
      ? `${d.start_date || '—'} → ${d.end_date || '—'}` : '',
    // presence requires BOTH, so the "passed" state is only claimed when neither
    // is blank (mirrors cst_validators: blank start OR end -> CSTNoDate).
    present: (d) => Boolean(d.start_date) && Boolean(d.end_date) },
  { key: 'total_aws_pre_approved_funding', label: 'Pre-approved Funding', sop: '§3 CST-1',
    introduced: '0.1.0', conditional: true,
    // Public-Sector-only. CSTNoWWPS = PS + blank (reject); CSTFundingAmountMissing
    // = sector unknown + blank (review). Commercial + blank is legitimately N/A.
    codes: ['CSTNoWWPS', 'CSTFundingAmountMissing'],
    value: (d) => d.total_aws_pre_approved_funding },
  // yes/no fields: `present` (= "satisfied") is ONLY YES. Without this,
  // `present` would fall back to Boolean(displayValue) and the truthy string
  // "NO" would read as satisfied — so a proven-version row with an ack/sig of
  // NO but no adverse finding would render a false green "Passed" (AutoSDE
  // CR-298110147). With present=NO->false, a NO value falls through to the
  // neutral "—" branch, and the backend finding (CSTPartnerAck / CSTNoSig / …)
  // is what turns it red when present.
  { key: 'partner_acknowledgment_completed', label: 'Partner Acknowledgment', sop: '§3 CST-2',
    introduced: '0.1.0',
    codes: ['CSTPartnerAck', 'CSTNoPartnerAck'],  // old code tolerated on historic rows
    value: (d) => yesNoDisplay(d.partner_acknowledgment_completed),
    present: (d) => yesNoDisplay(d.partner_acknowledgment_completed) === 'YES' },
  { key: 'customer_authorization_checkbox_confirmed', label: 'Customer Acknowledgment', sop: '§3 CST-2',
    introduced: '0.1.0',
    codes: ['CSTCustomerAck', 'CSTNoCustomerAck'],
    value: (d) => yesNoDisplay(d.customer_authorization_checkbox_confirmed),
    present: (d) => yesNoDisplay(d.customer_authorization_checkbox_confirmed) === 'YES' },
  { key: 'customer_authorization_signed', label: 'Customer Signature', sop: '§3 CST-2',
    introduced: '0.1.0', codes: ['CSTNoSig', 'CSTInvalidSigEmail'],
    value: (d) => yesNoDisplay(d.customer_authorization_signed),
    // yesNoDisplay maps yes_with_date -> YES, so signed states satisfy; NO does not.
    present: (d) => yesNoDisplay(d.customer_authorization_signed) === 'YES' },
  // The three CR-B forensic fields (introduced 0.3.0). Rendered only when the
  // CST carries the extracted enum OR the agent raised a finding for it
  // (optional:true) — so the tab stays clean on older rows validated before
  // CR-B shipped.
  { key: 'signature_authenticity', label: 'Signature Authenticity', sop: '§3 CST-2',
    introduced: '0.3.0', optional: true,
    // CSTSuspectedPastedSig: pre-0.5.0 rows (review-only pasted suspicion).
    codes: ['CSTInvalidSigPicture', 'CSTSigAuthenticityUnclear', 'CSTSuspectedPastedSig'],
    value: (d) => enumDisplay(d.signature_authenticity) },
  // How the signature was made + its certification proof (introduced 0.5.0,
  // POC_DECISION_RULES.md §1). Every signature rejection maps here too, so an
  // uncertified e-signature or a typed / pasted / email "signature" can never
  // read green on this row. "Passed" only for a determined method with no
  // finding (wet, or electronic/typed WITH proof) — see signatureSatisfied.
  { key: 'signature_method', label: 'Signature Method & Certification', sop: '§3 CST-2',
    introduced: '0.5.0', optional: true,
    codes: ['CSTInvalidSigNoCert', 'CSTInvalidSigTyped', 'CSTInvalidSigPicture',
      'CSTInvalidSigEmail', 'CSTSigAuthenticityUnclear'],
    value: (d) => signatureMethodDisplay(d),
    present: (d, entry) => signatureSatisfied(d, entry) },
  { key: 'template_version', label: 'Template Version', sop: '§3',
    introduced: '0.3.0', optional: true,
    codes: ['OldCST', 'CSTVersionUnverified', 'CSTVersionUnknown'],
    value: (d) => enumDisplay(d.template_version) },
  { key: 'template_conformance', label: 'Template Conformance', sop: '§3',
    introduced: '0.3.0', optional: true,
    codes: ['CSTEmailAttachment', 'AltCSTSuspected', 'CSTConformanceUnclear'],
    value: (d) => enumDisplay(d.template_conformance) },
];

/**
 * CST fields shown for reviewer context only — the SOP defines NO pass/fail on
 * them (source: poc_sop.yaml has no validation_taxonomy entry for these).
 */
export const POC_CST_INFO_FIELDS = [
  { key: 'customer_printed_name', label: 'Printed Name' },
  { key: 'customer_position_title', label: 'Position Title' },
  { key: 'customer_date_signed', label: 'Date Signed' },
  { key: 'docusign_envelope_id', label: 'DocuSign Envelope ID' },
  { key: 'signature_method_reason', label: 'Signature Method — Visual Cue' },
  { key: 'signature_page', label: 'Signature Page' },
  { key: 'signature_proof_location', label: 'Certification Proof Location' },
  { key: 'signature_pasted_signals', label: 'Pasted-Signature Signals' },
];

// Fields consumed by the two lists above (plus the date pair + `notes`, which is
// an internal extractor scratch field, not surfaced). Anything in the extracted
// CST data OUTSIDE this set is drift -> surfaced by collectOtherCstFields.
const _KNOWN_CST_DATA_KEYS = new Set([
  'project_name', 'claim_fund_request_id', 'start_date', 'end_date',
  'total_aws_pre_approved_funding', 'partner_acknowledgment_completed',
  'customer_authorization_checkbox_confirmed', 'customer_authorization_signed',
  'signature_authenticity', 'template_version', 'template_conformance',
  'customer_printed_name', 'customer_position_title', 'customer_date_signed',
  'docusign_envelope_id', 'notes',
  'signature_method', 'signature_proof_type', 'signature_method_reason',
  'signature_page', 'signature_proof_location', 'signature_pasted_signals',
]);

function yesNoDisplay(v) {
  const s = (v || '').toString().toLowerCase();
  if (['yes', 'yes_with_date', 'true'].includes(s)) return 'YES';
  if (['no', 'false'].includes(s)) return 'NO';
  return '';
}

// "docusign · proof: docusign envelope id" — the method plus, for an electronic
// or typed signature, which certification proof the CST itself carried ("none"
// when it carried none; proof in another attachment is in the findings).
function signatureMethodDisplay(d) {
  const method = enumDisplay(d.signature_method);
  if (!method) return '';
  const proof = enumDisplay(d.signature_proof_type);
  return proof ? `${method} · proof: ${proof}` : method;
}

// Signature-method values the agent emits (cst_validators._signature_findings).
const _SIG_ELECTRONIC_OR_TYPED = new Set([
  'docusign', 'electronic_tool', 'fill_and_sign_typed', 'fill_and_sign_drawn',
  'fill_and_sign_image', 'typed',
]);

/**
 * Is the signature affirmatively acceptable (so the row may read green)?
 *
 * The agent's own verdict wins when present: entry.signature_evidence.outcome
 * is "pass" only when the decision table accepted the signature — including an
 * e-signature whose proof was found in ANOTHER attachment (a separate
 * Certificate of Completion), which the CST's own signature_proof_type cannot
 * show. Without that verdict, fall back to the CST data alone: wet passes;
 * electronic/typed needs a real proof type; anything else (unclear, pasted,
 * email, unknown) never reads green. So a missing finding alone can never turn
 * an uncertified e-signature green (AutoSDE CR-313243742).
 */
function signatureSatisfied(d, entry) {
  const m = (d.signature_method || '').toString().toLowerCase();
  if (!m || m === 'unclear') return false;
  const outcome = entry && entry.signature_evidence && entry.signature_evidence.outcome;
  if (outcome) return outcome === 'pass';
  if (m === 'wet') return true;
  if (!_SIG_ELECTRONIC_OR_TYPED.has(m)) return false;
  const proof = (d.signature_proof_type || '').toString().toLowerCase();
  return Boolean(proof) && proof !== 'none';
}

function enumDisplay(v) {
  const s = (v == null ? '' : String(v)).trim();
  return s ? s.replace(/_/g, ' ') : '';
}

/**
 * Compare two dotted numeric versions ("0.3.0"). Returns -1 / 0 / 1.
 * Missing/short components read as 0; a non-parseable input returns null so the
 * caller can treat "can't compare" as "not proven".
 */
export function compareSemver(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return null;
  const pa = a.split('.').map((n) => parseInt(n, 10));
  const pb = b.split('.').map((n) => parseInt(n, 10));
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) return null;
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/**
 * Was `check.introduced` in force when a row validated at `sopVersion` was
 * produced? True only when sopVersion is a parseable version >= introduced.
 * A missing/unparseable sopVersion (older or unversioned row) -> false: the
 * check is NOT proven to have run, so the caller must not show green/N-A.
 */
function checkProvenToRun(introduced, sopVersion) {
  const cmp = compareSemver(sopVersion, introduced);
  return cmp !== null && cmp >= 0;
}

/**
 * Resolve every POC CST field check into a display row.
 *
 * Status per field is DRIVEN BY THE AGENT'S FINDINGS, gated by the row's
 * persisted SOP version so a green never overstates what actually ran:
 *   - an adverse finding whose code maps to the field (the check demonstrably
 *     ran and failed — ALWAYS trusted, no version gate):
 *       error   -> { type:'error',   statusLabel:'Failed' }
 *       warning -> { type:'warning', statusLabel:'Review' }
 *   - no adverse finding AND the check is proven to have run for this row
 *     (sop_version >= field.introduced):
 *       value present            -> { type:'success', statusLabel:'Passed' }
 *       conditional + blank      -> { type:'na',      statusLabel:'N/A' }
 *   - no adverse finding and the check is NOT proven to have run (older or
 *     unversioned row) -> { type:'unproven', statusLabel:'Present, not validated' }
 *     when a value exists, else { type:'neutral', statusLabel:'—' }.
 *     This is the batpur@ CR-298110147 fix: a populated value with no finding
 *     is NOT proof the check ran (e.g. template_version:"2024" or ack "NO" on a
 *     row produced before the check existed) — so it is never shown green.
 *
 * `optional` fields (the CR-B forensic enums) are omitted entirely when they
 * carry neither a value nor a finding, so older rows don't show empty rows.
 *
 * @param {object|null|undefined} cst  the customer_sign_off validations entry
 * @param {string|undefined} sopVersion  the row's persisted poc_sop version
 * @returns {Array<{key,label,sop,value,type,statusLabel,message}>}
 */
export function resolveCstFieldChecks(cst, sopVersion) {
  const entry = cst || {};
  const data = entry.data || {};
  const findings = Array.isArray(entry.findings) ? entry.findings : [];
  const byCode = {};
  findings.forEach((f) => { if (f && f.code && !(f.code in byCode)) byCode[f.code] = f; });

  const rows = [];
  POC_CST_CHECK_FIELDS.forEach((field) => {
    const hit = field.codes.map((c) => byCode[c]).find(Boolean) || null;
    const displayValue = (field.value(data) || '').toString();
    const present = field.present ? field.present(data, entry) : Boolean(displayValue);

    // Hide an optional row only when it has nothing to show: no finding AND no
    // value. A value that isn't "satisfied" (e.g. an uncertified e-signature)
    // still renders — neutral, never green.
    if (field.optional && !hit && !present && !displayValue) return;

    const proven = checkProvenToRun(field.introduced, sopVersion);

    let type;
    let statusLabel;
    if (hit) {
      // An adverse finding is direct evidence the check ran — trust it
      // regardless of version.
      type = hit.severity === 'error' ? 'error' : 'warning';
      statusLabel = hit.severity === 'error' ? 'Failed' : 'Review';
    } else if (!proven) {
      // No finding and we can't prove the check ran for this row. Never green.
      if (present) {
        type = 'unproven';
        statusLabel = 'Present, not validated';
      } else {
        type = 'neutral';
        statusLabel = '—';
      }
    } else if (present) {
      type = 'success';
      statusLabel = 'Passed';
    } else if (field.conditional) {
      type = 'na';
      statusLabel = 'N/A';
    } else {
      // Proven-to-run, no finding, blank, non-conditional: the field is empty
      // but nothing flagged it (e.g. a check that only fires on a populated
      // value). Neutral, not green.
      type = 'neutral';
      statusLabel = '—';
    }

    rows.push({
      key: field.key,
      label: field.label,
      sop: field.sop,
      value: displayValue || (type === 'na' ? 'Not applicable' : '—'),
      type,
      statusLabel,
      message: hit ? (hit.message || '') : '',
    });
  });
  return rows;
}

/**
 * The informational (not-validated-per-SOP) CST rows.
 * @param {object|null|undefined} cst
 * @returns {Array<{key,label,value}>}
 */
export function resolveCstInfoFields(cst) {
  const data = (cst || {}).data || {};
  return POC_CST_INFO_FIELDS.map((f) => ({
    key: f.key,
    label: f.label,
    value: (data[f.key] == null ? '' : String(data[f.key])) || '—',
  }));
}

/**
 * Drift guard: any key in the extracted CST data that neither the check-field
 * map nor the info-field list accounts for. Surfaced as a quiet note so a new
 * extracted field is never silently dropped into the "information" bucket
 * without a reviewer noticing it may need an SOP check. Values are stringified
 * and truncated for display; empty/blank fields are skipped.
 *
 * @param {object|null|undefined} cst
 * @returns {Array<{key,value}>}
 */
export function collectOtherCstFields(cst) {
  const data = (cst || {}).data || {};
  const out = [];
  Object.keys(data).forEach((k) => {
    if (_KNOWN_CST_DATA_KEYS.has(k)) return;
    const v = data[k];
    if (v == null || (typeof v === 'string' && v.trim() === '')) return;
    let str;
    try {
      str = typeof v === 'object' ? JSON.stringify(v) : String(v);
    } catch (e) {
      str = String(v);
    }
    if (str.length > 120) str = `${str.slice(0, 117)}…`;
    out.push({ key: k, value: str });
  });
  return out;
}

/**
 * Decide what the Claims tab should show when there is no `customer_sign_off`
 * entry in `attached_files`. A missing entry is NOT always a missing document
 * (batpur@ CR-295319668):
 *
 *   - `AttachmentEngineUnavailable` reason (decision flag_for_manual_review):
 *     the file-processing engine failed, so the CST could not be validated at
 *     all — an inability to inspect, not a definitive absence -> manual review.
 *   - explicit `NoCST` reason (reject): the CST is genuinely absent. This is
 *     checked BEFORE Corrupt because the backend can persist BOTH — a missing
 *     CST AND an unrelated quarantined file (batpur@ CR-295319668 rev 2). The
 *     actionable requirement ("provide a signed CST") must win, not be hidden
 *     behind a re-upload message about the other file.
 *   - `Corrupt` reason (reject): a CST-looking file was quarantined/unreadable
 *     — present but cannot be opened, not simply missing -> error, re-upload.
 *   - `CSTUnevaluated` reason (needs_review): a possible-CST attachment was
 *     left UNEVALUATED by the agent's bounded vision pass (sibling
 *     CR-296049312) — the CST may sit in that unexamined file, so the backend
 *     explicitly says "do not auto-reject as missing". Checked after the
 *     definitive NoCST/Corrupt reasons but BEFORE the missing() fallback, so it
 *     never overrides a genuine NoCST/Corrupt yet still avoids the false "no CST
 *     recognized" error on this path (batpur@ CR-295319668 rev 5).
 *   - otherwise: no CST classified and no disambiguating reason -> missing.
 *
 * @param {Array} reasons  results.reasons (array of { code, message, ... })
 * @returns {{ kind: 'engine_unavailable'|'missing'|'corrupt'|'unevaluated', alertType: 'warning'|'error', header: string, message: string }}
 */
export function resolveNoCstState(reasons) {
  const list = Array.isArray(reasons) ? reasons : [];
  const byCode = (code) => list.find((r) => r && r.code === code) || null;

  const engineUnavailable = byCode('AttachmentEngineUnavailable');
  if (engineUnavailable) {
    return {
      kind: 'engine_unavailable',
      alertType: 'warning',
      header: 'Attachments could not be inspected — manual review required',
      message:
        engineUnavailable.message ||
        'The file-processing engine was unavailable, so the Customer Sign-Off could not be validated. This is not a determination that the CST is missing. Manual review required.',
    };
  }

  // Explicit missing-CST wins over Corrupt: when both are present (CST absent
  // AND an unrelated file quarantined), the reviewer must still see the
  // requirement to provide a signed CST rather than only a re-upload prompt
  // for the other file.
  const missing = () => ({
    kind: 'missing',
    alertType: 'error',
    header: 'No Customer Sign-Off (CST) recognized',
    message:
      'No Customer Sign-Off Template was classified for this claim. A signed CST is required for a POC cash claim. See the Approval Summary tab for the full decision and required actions.',
  });

  if (byCode('NoCST')) {
    return missing();
  }

  const corrupt = byCode('Corrupt');
  if (corrupt) {
    return {
      kind: 'corrupt',
      alertType: 'error',
      header: 'Attachment could not be opened',
      message:
        corrupt.message ||
        'A required attachment could not be opened (quarantined or unreadable). Re-upload the document, confirm a successful upload, and resubmit.',
    };
  }

  // A possible-CST attachment the agent couldn't finish examining (sibling
  // CR-296049312's CSTUnevaluated). NOT a definitive absence — mirror the
  // engine-unavailable manual-review treatment rather than the red missing
  // error, matching the needs_review tab icon on this path.
  const unevaluated = byCode('CSTUnevaluated');
  if (unevaluated) {
    return {
      kind: 'unevaluated',
      alertType: 'warning',
      header: 'Customer Sign-Off could not be examined — manual review required',
      message:
        unevaluated.message ||
        'No Customer Sign-Off was recognized, but one or more attached documents could not be examined automatically. The CST may be present in an unexamined attachment — manual review required. Do not auto-reject as missing.',
    };
  }

  return missing();
}
